import { Text } from "@mantine/core";
import { KeyExposureChip } from "@/components/GenerationBadge";
import { Mono } from "@/components/Mono";
import { RelatedLink } from "@/components/RelatedLink";
import { StatusBadge } from "@/components/StatusBadge";
import { Timestamp } from "@/components/Timestamp";
import type { SessionKeyPermissionDoc, WithDocId } from "@/firestore/types";
import { humanize } from "@/lib/format";
import { flagLabel, keyExposure } from "@/wallet/generations";
import { col } from "../columns";

type Row = WithDocId<SessionKeyPermissionDoc>;

/**
 * Columns for `sessionKeyPermissions` rows: Wallet 360's Keys panel, and (with the wallet and
 * user) the fleet-wide exposed-keys list. `kmsKeyDestroyedAt` comes from these documents only.
 */
export function sessionKeyPermissionColumns({ withWallet }: { withWallet: boolean }) {
  const columns = [
    col<Row>({
      id: "id",
      header: "Key",
      value: (r) => r.id,
      cell: (r) => <RelatedLink entity="sessionKey" id={r.id ?? r._docId} />,
    }),
    col<Row>({
      id: "type",
      header: "Type",
      value: (r) => r.type,
      cell: (r) => <Mono value={r.type} copy={false} />,
    }),
    col<Row>({
      id: "account",
      header: "Account",
      value: (r) => r.targetSmartAccount,
      cell: (r) => <Text size="sm">{humanize(r.targetSmartAccount)}</Text>,
    }),
    col<Row>({
      id: "state",
      header: "State",
      value: (r) => r.state,
      cell: (r) => <StatusBadge state={r.state} />,
    }),
    col<Row>({
      id: "flag",
      header: "Flag",
      value: (r) => r.permissionFlag ?? "",
      cell: (r) => (
        <Text
          size="sm"
          ff={r.permissionFlag ? "monospace" : undefined}
          c={r.permissionFlag ? undefined : "dimmed"}
        >
          {flagLabel(r)}
        </Text>
      ),
    }),
    col<Row>({
      id: "signer",
      header: "Signer module",
      value: (r) => r.signerModuleAddress ?? "",
      cell: (r) =>
        r.signerModuleAddress ? (
          <Mono value={r.signerModuleAddress} short />
        ) : (
          <Text size="sm" c="dimmed">
            {r.enabledOnChainAt ? "stock ECDSA (legacy)" : "—"}
          </Text>
        ),
    }),
    col<Row>({
      id: "exposure",
      header: "Exposure",
      value: (r) => keyExposure(r).label,
      cell: (r) => <KeyExposureChip permission={r} />,
    }),
    col<Row>({
      id: "enabledOnChainAt",
      header: "Enabled on-chain",
      value: (r) => r.enabledOnChainAt ?? "",
      cell: (r) => <Timestamp value={r.enabledOnChainAt} />,
    }),
    col<Row>({
      id: "revokedAt",
      header: "Revoked",
      value: (r) => r.revokedAt ?? "",
      cell: (r) => <Timestamp value={r.revokedAt} />,
    }),
    col<Row>({
      id: "kmsKeyDestroyedAt",
      header: "KMS destroy scheduled",
      value: (r) => r.kmsKeyDestroyedAt ?? "",
      cell: (r) => <Timestamp value={r.kmsKeyDestroyedAt} />,
    }),
  ];
  if (!withWallet) return columns;
  return [
    columns[0],
    col<Row>({
      id: "wallet",
      header: "Wallet",
      value: (r) => r.account,
      cell: (r) => <RelatedLink entity="wallet" id={r.account} />,
    }),
    ...columns.slice(1),
    col<Row>({
      id: "owner",
      header: "User",
      value: (r) => r.owner,
      cell: (r) => <RelatedLink entity="user" id={r.owner} />,
    }),
  ];
}
